<template>
    <div class="vx-resource-set con-upload" ref="loader">
        <vx-image-preview
            :title="previewResource.filename || ''"
            :active.sync="previewActive"
            :src="previewResource.src || ''"
        ></vx-image-preview>
        <label v-if="label" class="vs-select--label">{{ label }}</label>
        <vx-scroll-area>
            <div
                @dragover.prevent="dragOver"
                @dragleave.prevent="dragLeave"
                @drop.prevent="receiveFile($event)"
            >
                <div :class="{ hidden: !hovering || !allowCreate }" class="overlay-wrapper">
                    <slot name="dropzone" :canDrop="canReceive">
                        <div v-if="canReceive" class="drag-overlay"></div>
                        <div v-else class="drag-overlay error">
                            <strong style="margin: 5px">{{ this.localMaxText }}</strong>
                        </div>
                    </slot>
                </div>

                <div class="con-img-upload" :class="justifyClass">
                    <transition-group appear name="list-complete" @before-leave="beforeLeave">
                        <div
                            v-for="(resource, index) in resources"
                            :key="resource.id || index"
                            class="img-upload"
                            :style="[
                                { transitionDelay: index * 0.1 + 's' },
                                { height: imageSize },
                                { width: imageSize }
                            ]"
                        >
                            <button
                                v-if="allowDelete"
                                class="btn-x-file"
                                type="button"
                                @click="remove(resource)"
                            >
                                <i translate="no" class="material-icons notranslate"> clear </i>
                            </button>
                            <button
                                class="btn-upload-file"
                                :class="allowSelect ? '' : 'unselectable'"
                                @click="select(resource)"
                            >
                                <i
                                    v-if="allowSelect"
                                    translate="no"
                                    class="material-icons notranslate"
                                >
                                    {{ selectIcon }}
                                </i>
                            </button>
                            <img
                                v-if="resource.mimeType.startsWith('image')"
                                :key="index"
                                :src="resource.src"
                                class="image-preview"
                                @touchend="viewImage(resource, $event)"
                                @click="viewImage(resource, $event)"
                            />
                            <div
                                v-else
                                class="flex flex-col content-center items-center text-center"
                                @click="download(resource)"
                            >
                                <feather-icon icon="FileTextIcon" svgClasses="h-16 w-16" />
                                <span>{{ resource.filename }}</span>
                            </div>
                        </div>
                        <div
                            v-if="showUploader"
                            key="uploader"
                            class="con-input-upload"
                            :style="[
                                { transitionDelay: resources.length * 0.1 + 's' },
                                { height: imageSize },
                                { width: imageSize }
                            ]"
                        >
                            <input
                                ref="fileInput"
                                v-bind="$attrs"
                                :multiple="limit == null || limit > 1"
                                type="file"
                                @change="receiveFile"
                            />
                            <span class="text-input">
                                {{ text }}
                            </span>
                            <button
                                :disabled="true"
                                type="button"
                                title="Upload"
                                class="btn-upload-all vs-upload--button-upload"
                            >
                                <i translate="no" class="material-icons notranslate">
                                    cloud_upload
                                </i>
                            </button>
                        </div>
                    </transition-group>
                </div>
            </div>
        </vx-scroll-area>
    </div>
</template>

<script>
export default {
    name: 'vx-resource-set',
    props: {
        resources: {
            type: Array,
            default: () => [],
            validator: function (value) {
                return value.every(
                    (r) =>
                        typeof r === 'object' &&
                        r.hasOwnProperty('id') &&
                        r.hasOwnProperty('src') &&
                        r.hasOwnProperty('mimeType') &&
                        r.hasOwnProperty('filename')
                )
            }
        },
        text: {
            type: String,
            default: 'Upload a File'
        },
        maxText: {
            type: String,
            default: 'Upload a File'
        },
        limit: {
            type: [Number, String],
            default: null
        },
        label: {
            type: String,
            default: null
        },
        allowTypes: {
            type: [String, Array],
            default: () => []
        },
        imageSize: {
            type: [Number, String],
            default: '200px'
        },
        allowCreate: {
            type: Boolean,
            default: false
        },
        allowDelete: {
            type: Boolean,
            default: false
        },
        hardDelete: {
            type: Boolean,
            default: false
        },
        hardCreate: {
            type: Boolean,
            default: false
        },
        selectIcon: {
            type: String,
            default: 'cloud_upload'
        },
        justify: {
            type: String,
            default: 'left',
            validator: function (value) {
                return ['left', 'right', 'center', 'spaced'].includes(value)
            }
        }
    },
    data() {
        return {
            hovering: false,
            incomingFiles: 0,
            previewActive: false,
            previewResource: {}
        }
    },
    computed: {
        showUploader() {
            return this.allowCreate && (this.limit == null || this.resources.length < this.limit)
        },
        allowSelect() {
            return this.$listeners['selected'] != null
        },
        localMaxText() {
            if (this.maxText) {
                this.maxText
            }
            return `Restricted to ${this.limit} files`
        },
        canReceive() {
            if (this.limit == null) {
                return true
            }
            return this.resources.length + this.incomingFiles <= this.limit
        },
        types() {
            if (!this.allowTypes) {
                return []
            } else if (typeof this.allowTypes === 'string') {
                return [this.allowTypes]
            }
            return this.allowTypes
        },
        justifyClass() {
            switch (this.justify) {
                case 'center':
                    return 'justify-around'
                case 'right':
                    return 'justify-end'
                case 'spaced':
                    return 'justify-around'
                default:
                    return 'justify-start'
            }
        }
    },
    methods: {
        beforeLeave(el) {
            const { marginLeft, marginTop, width, height } = window.getComputedStyle(el)

            el.style.left = `${el.offsetLeft - parseFloat(marginLeft, 10)}px`
            el.style.top = `${el.offsetTop - parseFloat(marginTop, 10)}px`
            el.style.width = width
            el.style.height = height
        },
        download(resource) {
            try {
                this.$api.resources.download(resource)
            } catch (err) {
                this.notifyServerError(err)
            }
        },
        viewImage(resource) {
            this.previewActive = true
            this.previewResource = resource
            this.previewResource.src = this.previewResource.src.replace('/thumbnail', '')
        },
        select(resource) {
            if (this.allowSelect) {
                this.$emit('selected', resource)
            }
        },
        dragOver(event) {
            this.hovering = true
            var fileCount = 0
            event.dataTransfer.items.forEach((f) => {
                if (f.kind === 'file') {
                    fileCount++
                }
            })
            this.incomingFiles = fileCount
        },
        dragLeave(event) {
            this.hovering = false
            this.incomingFiles = 0
        },
        async receiveFile(event) {
            try {
                this.hovering = false
                var files = []

                // Check which event type for file source
                if (event.dataTransfer) {
                    files = event.dataTransfer.files
                } else if (event.target) {
                    files = event.target.files
                } else {
                    throw 'No files found'
                }

                // Validate file limit
                if (this.limit && this.resources.length + files.length > this.limit) {
                    this.notifyError(
                        'Limit Exceeded',
                        `Restricted to a maximum of ${this.limit} files.`,
                        6000
                    )
                    return
                }

                // "borrow" array methods for FileList object
                var file_array = Array.from(files)

                var errored_file = file_array.find((f) => {
                    return !this.checkType(f)
                })

                if (errored_file) {
                    this.notifyError(
                        'Invalid Type',
                        `Uploader only accepts ${this.types.textualize(
                            'or'
                        )} files. Cannot upload: ${errored_file.name}.`,
                        8000
                    )
                    return
                }

                this.startLoad()
                await Promise.allSettled(file_array.map((file) => this.upload(file)))
            } finally {
                this.endLoad()
                this.incomingFiles = 0
            }
        },
        async upload(file) {
            var resource = null
            try {
                if (this.hardCreate) {
                    resource = await this.$api.resources.create(file)
                } else {
                    resource = {
                        filename: file.name,
                        mimeType: file.type,
                        id: null,
                        src: await file.toBase64(),
                        file: file
                    }
                }

                // Allows bound listener to run synchronously before transaction is considered complete
                if (this.$listeners['uploaded']) {
                    var prom = this.$listeners['uploaded'](resource)
                    if (prom && prom.then) {
                        await prom
                    }
                }
                if (!this.resources.any((r) => r.id == resource.id)) {
                    this.resources.push(resource)
                }
            } catch (err) {
                if (resource) {
                    await this.$api.resources.delete(resource.id)
                }
                this.notifyServerError(err)
                // this.notifyError(
                //     'Upload Failed',
                //     'An error occurred while trying to complete your request.\n Please check your internet and try again.'
                // )
            }
        },
        async remove(resource) {
            try {
                if (this.$listeners['deleted'] && resource.id) {
                    var prom = this.$listeners['deleted'](resource)
                    if (prom && prom.then) {
                        await prom
                    }
                }
                this.resources.removeWhere((r) => r.id == resource.id)

                if (this.hardDelete) {
                    this.$api.resources.delete(resource.id)
                }
            } catch (err) {
                this.notifyServerError(err)
                // if (err != 404) {
                //     this.notifyError(
                //         'Delete Failed',
                //         'An error occurred while trying to complete your request.\n Please check your internet and try again.'
                //     )
                // }
            }
        },
        checkType(file) {
            if (this.types.any()) {
                return this.types.any((t) => {
                    return (
                        file.type && (file.type.startsWith(`${t}/`) || file.type.endsWith(`/${t}`))
                    )
                })
            }
            return true
        }
    }
}
</script>

<style scoped>
.list-complete-enter {
    opacity: 0;
    transform: translateY(-60px);
}
.list-complete-leave-to {
    opacity: 0;
    transform: translateY(60px);
}
.list-complete-leave-active {
    transition-delay: 0 !important;
    position: absolute;
    z-index: 100000;
}

.image-popup {
    z-index: 54000;
}
.image-popup.zoomed ::v-deep .vs-popup {
    transform: scale(2);
}
.image-popup ::v-deep img {
    cursor: zoom-in;
}
.image-popup.zoomed ::v-deep img {
    cursor: zoom-out;
}
.con-img-upload {
    display: flex;
    flex-wrap: wrap;
    border: 2px dashed rgba(0, 0, 0, 0.1);
}
.btn-upload-file span {
    opacity: 1;
    position: absolute;
    top: 12%;
    padding: 20px;
    transition: all 0.2s ease;
    z-index: 50;
    border-radius: 50%;
    font-size: 1.5rem;
}
.con-input-upload:hover {
    transform: scale(0.98);
}
.unselectable {
    pointer-events: none;
}
.image-preview {
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    max-width: 100%;
    max-height: 100%;
}
.hidden {
    display: none;
}
.overlay-wrapper {
    pointer-events: none;
    position: absolute;
    height: 98%;
    width: 100%;
    z-index: 10000;
}
.drag-overlay.error {
    border: 2px dashed rgba(var(--vs-danger), 0.5);
    background-color: rgba(var(--vs-danger), 0.1);
}
.drag-overlay {
    border: 2px dashed rgba(var(--vs-primary), 0.5);
    background-color: rgba(var(--vs-primary), 0.1);
    opacity: 1;
    height: 100%;
    width: 100%;
    border-radius: 10px;
}
</style>
